/**
 * Ontology Category Definitions
 * Maps classes to logical categories for better organization and filtering
 */

import type { OntologyDomain } from '@/types/ontology'

export interface OntologyCategory {
  id: string
  label: string
  domain: OntologyDomain
  description: string
  classLocalNames: string[] // Class localNames that belong to this category
  color?: string
}

// Category definitions organized by domain
export const ONTOLOGY_CATEGORIES: OntologyCategory[] = [
  // ========================================================================
  // MARKET DOMAIN CATEGORIES
  // ========================================================================
  {
    id: 'market-structure',
    label: 'Market Structure',
    domain: 'market',
    description: 'Industry landscape, market opportunities, and regulations',
    classLocalNames: ['Industry', 'Market', 'MarketOpportunity', 'Regulation'],
  },
  {
    id: 'competition',
    label: 'Competition',
    domain: 'market',
    description: 'Competitors, competitive forces, and alternatives',
    classLocalNames: [
      'Competitor',
      'CompetitiveForce',
      'Alternative',
      'AlternativeAdvantage',
      'AlternativeDisadvantage',
    ],
  },
  {
    id: 'market-signals',
    label: 'Market Signals',
    domain: 'market',
    description: 'Trends and market signals',
    classLocalNames: ['Trend', 'MarketSignal'],
  },
  {
    id: 'customer-profiles',
    label: 'Customer Profiles',
    domain: 'market',
    description: 'Customer segments, personas, and stakeholders',
    classLocalNames: [
      'CustomerSegment',
      'Persona',
      'Person',
      'Stakeholder',
      'Demographic',
      'Psychographic',
    ],
  },
  {
    id: 'customer-experience',
    label: 'Customer Experience',
    domain: 'market',
    description: 'Journey mapping and touchpoints',
    classLocalNames: ['CustomerJourney', 'JourneyStage', 'Touchpoint'],
  },
  {
    id: 'customer-needs',
    label: 'Customer Needs',
    domain: 'market',
    description: 'Problems, pains, gains, jobs, and needs',
    classLocalNames: [
      'Problem',
      'CustomerPain',
      'CustomerGain',
      'CustomerJob',
      'CustomerNeed',
      'Workaround',
    ],
  },

  // ========================================================================
  // BUSINESS DOMAIN CATEGORIES
  // ========================================================================
  {
    id: 'business-model-canvas',
    label: 'Business Model Canvas',
    domain: 'business',
    description: 'Business model components (BMC framework)',
    classLocalNames: [
      'Company',
      'BusinessModel',
      'ValueProposition',
      'Channel',
      'CustomerRelationship',
      'RevenueStream',
      'KeyResource',
      'KeyActivity',
      'KeyPartnership',
      'CostStructure',
    ],
  },
  {
    id: 'strategy-goals',
    label: 'Strategy & Goals',
    domain: 'business',
    description: 'Strategic planning and objectives',
    classLocalNames: ['Strategy', 'StrategicGoal', 'Decision', 'Assumption'],
  },
  {
    id: 'swot-analysis',
    label: 'SWOT Analysis',
    domain: 'business',
    description: 'Strengths, weaknesses, opportunities, and threats',
    classLocalNames: ['Strength', 'Weakness', 'Opportunity', 'Threat'],
  },
  {
    id: 'go-to-market',
    label: 'Go-to-Market',
    domain: 'business',
    description: 'Market entry and positioning',
    classLocalNames: ['GoToMarketStrategy', 'MarketingCampaign', 'PositioningStatement'],
  },
  {
    id: 'tracking-validation',
    label: 'Tracking & Validation',
    domain: 'business',
    description: 'Metrics, KPIs, and validation',
    classLocalNames: ['Metric', 'KPI', 'Hypothesis', 'Experiment', 'ValidationResult'],
  },

  // ========================================================================
  // SOLUTION DOMAIN CATEGORIES
  // ========================================================================
  {
    id: 'product-vision',
    label: 'Product Vision',
    domain: 'solution',
    description: 'The to-be state: products, capabilities, features, and value creation mechanisms',
    classLocalNames: ['Product', 'Service', 'SolutionVision', 'SolutionStrategy', 'ProductVision', 'ProductGoal', 'Capability', 'Feature', 'PainReliever', 'GainCreator'],
  },
  {
    id: 'product-requirements',
    label: 'Product Requirements',
    domain: 'solution',
    description: 'Epics, functional requirements, non-functionals, constraints, and acceptance criteria',
    classLocalNames: [
      'Epic',
      'UserStory',
      'Requirement',
      'FunctionalRequirement',
      'NonFunctionalRequirement',
      'Constraint',
      'AcceptanceCriteria',
    ],
  },
  {
    id: 'roadmap-planning',
    label: 'Roadmap & Planning',
    domain: 'solution',
    description: 'Themes, releases, and roadmap planning',
    classLocalNames: ['Theme', 'Release', 'Sprint', 'Milestone'],
  },
  {
    id: 'quality-testing',
    label: 'Quality & Testing',
    domain: 'solution',
    description: 'Testing, quality attributes, and constraints',
    classLocalNames: [
      'TestCase',
      'TestSuite',
      'QualityAttribute',
      'Constraint',
      'TechnicalDebt',
    ],
  },
  {
    id: 'architecture',
    label: 'Architecture',
    domain: 'solution',
    description: 'Technical architecture and components',
    classLocalNames: [
      'Architecture',
      'Component',
      'Interface',
      'Module',
      'Service',
      'API',
    ],
  },
  {
    id: 'work-management',
    label: 'Work Management',
    domain: 'solution',
    description: 'Tasks, bugs, and work items',
    classLocalNames: ['Task', 'Bug', 'Issue', 'WorkItem'],
  },

  // ========================================================================
  // CORE DOMAIN CATEGORIES
  // ========================================================================
  {
    id: 'insights',
    label: 'Insights & Sources',
    domain: 'core',
    description: 'Extracted insights and source documents',
    classLocalNames: ['Facet', 'Source', 'Document'],
  },
]

/**
 * Get category for a class by its localName
 */
export function getCategoryForClass(classLocalName: string): OntologyCategory | undefined {
  return ONTOLOGY_CATEGORIES.find((cat) => cat.classLocalNames.includes(classLocalName))
}

/**
 * Get all categories for a specific domain
 */
export function getCategoriesForDomain(domain: OntologyDomain): OntologyCategory[] {
  return ONTOLOGY_CATEGORIES.filter((cat) => cat.domain === domain)
}

/**
 * Get category statistics (class counts per category)
 */
export function getCategoryStats(
  classLocalNames: string[]
): Record<string, { category: OntologyCategory; count: number }> {
  const stats: Record<string, { category: OntologyCategory; count: number }> = {}

  ONTOLOGY_CATEGORIES.forEach((category) => {
    const count = classLocalNames.filter((name) =>
      category.classLocalNames.includes(name)
    ).length

    if (count > 0) {
      stats[category.id] = { category, count }
    }
  })

  return stats
}
